import React, { useEffect, useState } from "react";
import { api, Modal } from "../../../services/common";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Select, MenuItem, FormControl, InputLabel,
    Box, TextField, Button, Typography, Pagination
} from "@mui/material";
import EditLeadModal from "./EditLeadModal";

const ViewLead = () => {
    const [leads, setLeads] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Tutors
    const [tutors, setTutors] = useState([]);

    // Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [tutorFilter, setTutorFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState("");
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);

    // Sorting
    const [sortOrder, setSortOrder] = useState("desc");

    // Pagination
    const [limit, setLimit] = useState(50);
    const [page, setPage] = useState(1);

    const [sortKey, setSortKey] = useState("createdAt"); // default sorting by createdAt

    const handleSortToggle = (key) => {
        if (sortKey === key) {
            // Toggle order if sorting same column
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            // Change column and reset order to desc
            setSortKey(key);
            setSortOrder("desc");
        }
    };

    // Modal
    const [selectedLead, setSelectedLead] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const openEditModal = (lead) => {
        setSelectedLead(lead);
        setShowEditModal(true);
    };


    const fetchAvailableSlots = async (tutorId) => {
        try {
            setLoadingSlots(true);
            const res = await api.get(`/tutors/view-available-schedule/${tutorId}?demo=true`);
            setAvailableSlots(res.data || []);
            setShowScheduleModal(true);
        } catch (error) {
            console.error("Error fetching slots:", error);
        } finally {
            setLoadingSlots(false);
        }
    };



    const handleAddStudentsToSchedule = async () => {
        try {
            await api.post(`/tutors/add-to-schedule/${tutorFilter}`, {
                slotId: selectedSlot,
                students: selectedStudents
            });
            setShowScheduleModal(false);
            setSelectedStudents([]);
            fetchLeads(); // refresh
        } catch (error) {
            console.error("Error adding students:", error);
        }
    };
    const fetchTutors = async () => {
        try {
            const res = await api.get(`/tutors/view-tutors`);
            setTutors(res.data || []);
        } catch (error) {
            console.error("Error fetching tutors:", error);
        }
    };

    const fetchLeads = async () => {
        try {
            let params = new URLSearchParams();

            if (statusFilter) params.append("status", statusFilter);
            if (tutorFilter) params.append("tutorId", tutorFilter); // sending tutorId
            if (sourceFilter) params.append("source", sourceFilter);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);
            if (sortOrder) params.append("sort", sortOrder);
            if (sortKey) params.append("sortKey", sortKey);

            // Pagination
            const skip = (page - 1) * limit;
            params.append("skip", skip);
            params.append("limit", limit);

            const response = await api.get(`/common/leads?${params.toString()}`);

            // Backend should send { leads: [], totalCount: number }
            setLeads(response.data.leads || []);
            setTotalCount(response.data.totalCount || 0);
        } catch (error) {
            console.error("Error fetching leads:", error);
        }
    };

    useEffect(() => {
        fetchTutors();
    }, []);

    useEffect(() => {
        fetchLeads();
    }, [statusFilter, tutorFilter, sourceFilter, startDate, endDate, sortOrder, page, limit]);


    return (
        <div>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Leads ({totalCount})
            </Typography>

            {/* Filters */}
            <Box sx={{ display: "flex", gap: 2, marginBottom: 2, flexWrap: "wrap" }}>
                {/* Status Filter */}
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="contacted">Contacted</MenuItem>
                        <MenuItem value="qualified">Qualified</MenuItem>
                        <MenuItem value="lost">Lost</MenuItem>
                    </Select>
                </FormControl>

                {/* Tutor Filter */}
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="tutor-filter-label">Filter by Tutor</InputLabel>
                    <Select
                        labelId="tutor-filter-label"
                        value={tutorFilter}
                        onChange={(e) => setTutorFilter(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="">All Tutors</MenuItem>
                        {tutors.map((tutor) => (
                            <MenuItem key={tutor.id} value={tutor.id}>
                                {tutor.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Source Filter */}
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="source-filter-label">Filter by Source</InputLabel>
                    <Select
                        labelId="source-filter-label"
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="phone">Phone</MenuItem>
                        <MenuItem value="form">Form</MenuItem>
                        <MenuItem value="website">Website</MenuItem>
                    </Select>
                </FormControl>

                {/* Date Range */}
                <TextField
                    label="Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                <TextField
                    label="End Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />

                {/* Limit Dropdown */}
                <FormControl sx={{ minWidth: 100 }}>
                    <InputLabel id="limit-label">Rows</InputLabel>
                    <Select
                        labelId="limit-label"
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1); // reset to first page
                        }}
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                    </Select>
                </FormControl>

                <Button variant="outlined" onClick={fetchLeads}>
                    Apply Filters
                </Button>

                {tutorFilter && selectedStudents.length > 0 && (
    <Button
        variant="contained"
        color="primary"
        onClick={() => {
           
            fetchAvailableSlots(tutorFilter);
        }}
    >
        Add to Schedule
    </Button>
)}
            </Box>

            {/* Leads Table */}
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 500 }} aria-label="leads table">
                    <TableHead>
                        <TableRow>
                        <TableCell align="left">
    <input
        type="checkbox"
        checked={selectedStudents.length > 0 && selectedStudents.length === leads.length}
        onChange={(e) => {
            if (e.target.checked) {
                setSelectedStudents(leads.map((lead) => lead.id));
            } else {
                setSelectedStudents([]);
            }
        }}
    />
</TableCell>
                            <TableCell align="left">Name</TableCell>
                            <TableCell align="left">Email</TableCell>
                            <TableCell align="left">Phone Number</TableCell>
                            <TableCell align="left">
                                Date Added{" "}
                                <Button size="small" onClick={() => handleSortToggle("createdAt")}>
                                    {sortOrder === "asc" ? "↑" : "↓"}
                                </Button>
                            </TableCell>
                            <TableCell align="left">Tutor</TableCell>
                            <TableCell align="left">Course</TableCell>
                            <TableCell align="left">Source</TableCell>
                            <TableCell align="left">Status</TableCell>
                            <TableCell align="left">UTM Source</TableCell>
                            <TableCell align="left">
                                Updated At{" "}
                                <Button size="small" onClick={() => handleSortToggle("updatedAt")}>
                                    {sortKey === "updatedAt" ? (sortOrder === "asc" ? "↑" : "↓") : "↕"}
                                </Button>
                            </TableCell>
                            <TableCell align="left">Edit</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leads.length > 0 ? (
                            leads.map((lead) => (
                                <TableRow key={lead.id}>
                                    <TableCell align="left">
                                        <input
                                            type="checkbox"
                                            checked={selectedStudents.includes(lead.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedStudents((prev) => [...prev, lead.id]);
                                                } else {
                                                    setSelectedStudents((prev) => prev.filter((id) => id !== lead.id));
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="left">{lead.name}</TableCell>
                                    <TableCell align="left">{lead.email}</TableCell>
                                    <TableCell align="left">{lead.phone}</TableCell>
                                    <TableCell align="left">
                                        {new Date(lead.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell align="left">
                                        {lead.tutor}
                                    </TableCell>
                                    <TableCell align="left">{lead.course}</TableCell>
                                    <TableCell align="left">{lead.source}</TableCell>
                                    <TableCell align="left">{lead.status}</TableCell>
                                    <TableCell align="left">{lead?.utm_source}</TableCell>
                                    <TableCell align="left">
                                        {lead.updatedAt ? new Date(lead.updatedAt).toLocaleDateString() : "-"}
                                    </TableCell>
                                    <TableCell align="left">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => openEditModal(lead)}
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    No leads available
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Pagination
                    count={Math.ceil(totalCount / limit)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                />
            </Box>

            <EditLeadModal
                open={showEditModal}
                onClose={() => setShowEditModal(false)}
                lead={selectedLead}
                refreshLeads={fetchLeads}
            />

<Modal
  show={showScheduleModal}
  onClose={() => setShowScheduleModal(false)}
  aria-labelledby="select-slot-title"
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "background.paper",
      boxShadow: 24,
      p: 4,
      width: 500,
      maxHeight: "80vh",
      overflowY: "auto",
      borderRadius: 2
    }}
  >
    <Typography id="select-slot-title" variant="h6" sx={{ mb: 2 }}>
      Select Available Slot
    </Typography>

    {loadingSlots ? (
      <Typography>Loading slots...</Typography>
    ) : (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {availableSlots.map((slot) => (
          <Paper
            key={slot.id}
            sx={{
              p: 2,
              border: selectedSlot === slot.id ? "2px solid blue" : "1px solid #ccc",
              cursor: "pointer",
              minWidth: "120px"
            }}
            onClick={() => setSelectedSlot(slot.id)}
          >
            <Typography>{slot.date}</Typography>
            <Typography variant="body2">{slot.time}</Typography>
          </Paper>
        ))}
      </div>
    )}

    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
      <Button onClick={() => setShowScheduleModal(false)}>Cancel</Button>
      <Button
        variant="contained"
        disabled={!selectedSlot}
        onClick={handleAddStudentsToSchedule}
      >
        Confirm
      </Button>
    </Box>
  </Box>
</Modal>



        </div>
    );
};

export default ViewLead;
